import { useState, useRef, ComponentType } from 'react';
import { useAppState } from '../../../state';
import { isSupported } from '@twilio/video-processors';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import useChatContext from '../../../hooks/useChatContext/useChatContext';
import { useTranslation } from 'react-i18next';
import Button, { ButtonProps } from '@mui/material/Button';
import DeviceSelectionDialog from '../../DeviceSelectionDialog/DeviceSelectionDialog';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuContainer from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreIcon from '@mui/icons-material/MoreVert';
import Typography from '@mui/material/Typography';
import { Tooltip } from '@dtsl/react';
import { Theme, useMediaQuery } from '@mui/material';
import { VideoRoomMonitor } from '@twilio/video-room-monitor';

export default function Menu(props: { CustomToggleButton: ComponentType<ButtonProps> }) {
  const { setIsGalleryViewActive, isGalleryViewActive } = useAppState();
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { CustomToggleButton: ToggleButton } = props;

  const { setIsChatWindowOpen } = useChatContext();
  const { setIsBackgroundSelectionOpen } = useVideoContext();

  const anchorRef = useRef<HTMLSpanElement>(null);

  return (
    <>
      {/* This span element allows the menu to be anchored to the button, as this
      could not be achieved by anchoring it to the button due to this one having a tooltip */}
      <span ref={anchorRef}></span>
      <Tooltip text={t('ROOM.OPTIONS_TOOLTIP')} placement="top">
        {isMobile ? (
          <Button id="video-calls-menu" onClick={() => setMenuOpen((isOpen) => !isOpen)}>
            <MoreIcon style={{ color: '#fff' }} />
          </Button>
        ) : (
          <ToggleButton
            id="video-calls-menu"
            onClick={() => setMenuOpen((isOpen) => !isOpen)}
            startIcon={isMobile ? <MoreIcon style={{ color: '#fff' }} /> : <SettingsIcon style={{ color: '#fff' }} />}
          >
            {t('ROOM.OPTIONS')}
          </ToggleButton>
        )}
      </Tooltip>
      <MenuContainer
        open={menuOpen}
        onClose={() => setMenuOpen((isOpen) => !isOpen)}
        anchorEl={anchorRef.current}
        transformOrigin={{
          vertical: isMobile ? -55 : 210,
          horizontal: 'center',
        }}
      >
        <MenuItem
          id="video-calls-help"
          onClick={() => {
            window.open(
              'https://help.brevo.com/hc/en-us/articles/7848887055378-Use-Brevo-Zoom-or-Google-Meet-as-a-video-call-tool#h_01H90WNJW8SHBM75P6HHHB2PS3',
              '_blank',
              'noopener,noreferrer'
            );
            setMenuOpen(false);
          }}
        >
          <Typography variant="body1">{t('ROOM.HELP')}</Typography>
        </MenuItem>
        <MenuItem
          id="video-calls-settings"
          onClick={() => {
            setSettingsOpen(true);
            setMenuOpen(false);
          }}
        >
          <Typography variant="body1">{t('ROOM.SETTINGS')}</Typography>
        </MenuItem>
        {isSupported && (
          <MenuItem
            id="video-calls-backgrounds"
            onClick={() => {
              setIsBackgroundSelectionOpen(true);
              setIsChatWindowOpen(false);
              setMenuOpen(false);
            }}
          >
            <Typography variant="body1">{t('ROOM.BACKGROUNDS')}</Typography>
          </MenuItem>
        )}
        <MenuItem
          id="video-calls-troubleshoot"
          onClick={() => {
            VideoRoomMonitor.toggleMonitor();
            setMenuOpen(false);
          }}
        >
          <Typography variant="body1">{t('ROOM.MONITOR')}</Typography>
        </MenuItem>

        <MenuItem
          id="video-calls-toggle-view"
          onClick={() => {
            setIsGalleryViewActive((isGallery) => !isGallery);
            setMenuOpen(false);
          }}
        >
          <Typography variant="body1">{isGalleryViewActive ? t('ROOM.SPEAKER') : t('ROOM.GALLERY')}</Typography>
        </MenuItem>
      </MenuContainer>
      <DeviceSelectionDialog
        open={settingsOpen}
        onClose={() => {
          setSettingsOpen(false);
          setMenuOpen(false);
        }}
      />
    </>
  );
}
